import { Theme, css } from '@emotion/react';

type IconButtonColor = 'primary' | 'secondary' | 'danger' | 'tertiary';

const createContainedVariant = (colors: Theme['colors'], colorName: IconButtonColor) => {
  const colorMap = {
    primary: {
      bg: colors.backgroundPrimary,
      fg: colors.textWhite,
      hover: colors.backgroundPrimaryLight,
    },
    secondary: {
      bg: colors.backgroundDark,
      fg: colors.textWhite,
      hover: colors.backgroundDarker,
    },
    danger: {
      bg: colors.backgroundDanger,
      fg: colors.textWhite,
      hover: colors.backgroundDanger,
    },
    tertiary: {
      bg: colors.backgroundBase,
      fg: colors.textBase,
      hover: colors.backgroundDark,
    },
  };

  const colors_ = colorMap[colorName];

  return css`
    background-color: ${colors_.bg};
    color: ${colors_.fg};
    &:hover {
      background: ${colors_.hover};
    }
    &:disabled {
      background: ${colors.disabledBackground};
      color: ${colors.disabled};
    }
  `;
};

const createOutlinedVariant = (colors: Theme['colors'], colorName: IconButtonColor) => {
  const colorMap = {
    primary: {
      border: colors.borderPrimary,
      fg: colors.iconPrimary,
      hover: colors.backgroundPrimaryLighter,
    },
    secondary: {
      border: colors.borderBase,
      fg: colors.iconBase,
      hover: colors.backgroundLighter,
    },
    danger: {
      border: colors.borderDanger,
      fg: colors.iconDanger,
      hover: colors.backgroundDangerLighter,
    },
    tertiary: {
      border: colors.borderBase,
      fg: colors.iconBase,
      hover: colors.backgroundLight,
    },
  };

  const colors_ = colorMap[colorName];

  return css`
    border: 1px solid ${colors_.border};
    color: ${colors_.fg};
    padding: 3px;
    &:hover {
      background: ${colors_.hover};
    }
    &:disabled {
      border-color: ${colors.borderLight};
      color: ${colors.disabled};
    }
  `;
};

const createTextVariant = (colors: Theme['colors'], colorName: IconButtonColor) => {
  const colorMap = {
    primary: {
      fg: colors.iconPrimary,
      hover: colors.backgroundPrimaryLighter,
    },
    secondary: {
      fg: colors.iconBase,
      hover: colors.backgroundLighter,
    },
    danger: {
      fg: colors.iconDanger,
      hover: colors.backgroundDangerLighter,
    },
    tertiary: {
      fg: colors.iconBase,
      hover: colors.backgroundLight,
    },
  };

  const colors_ = colorMap[colorName];

  return css`
    color: ${colors_.fg};
    &:hover {
      background: ${colors_.hover};
    }
    &:disabled {
      color: ${colors.disabled};
    }
  `;
};

export const commonStyles = (theme: Theme) => css`
  border-radius: ${theme.shape.borderRadius}px;
  padding: ${theme.spacing(0.5)};
  font-size: 2.8rem;

  &:focus {
    box-shadow: none;
  }
  & .MuiTouchRipple-child {
    border-radius: ${theme.shape.borderRadius}px;
  }
`;

export const iconButtonVariantStyles = (theme: Theme) => ({
  containedPrimary: createContainedVariant(theme.colors, 'primary'),
  containedSecondary: createContainedVariant(theme.colors, 'secondary'),
  containedDanger: createContainedVariant(theme.colors, 'danger'),
  containedTertiary: createContainedVariant(theme.colors, 'tertiary'),

  outlinedPrimary: createOutlinedVariant(theme.colors, 'primary'),
  outlinedSecondary: createOutlinedVariant(theme.colors, 'secondary'),
  outlinedTertiary: createOutlinedVariant(theme.colors, 'tertiary'),
  outlinedDanger: createOutlinedVariant(theme.colors, 'danger'),

  textPrimary: createTextVariant(theme.colors, 'primary'),
  textSecondary: createTextVariant(theme.colors, 'secondary'),
  textTertiary: createTextVariant(theme.colors, 'tertiary'),
  textDanger: createTextVariant(theme.colors, 'danger'),
});
